import router from '../../router';
import store from '../../store';
import { i18n } from '../../localization';
import isValid from 'date-fns/isValid';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import { ERROR } from '../../constants';

/**
 * Common unsaved state handler
 * @param {Object} confirmation the object contains confirmTitle, confirmBody, confirmOk, confirmCancel
 * @returns {Promise}
 */
export function unsavedHandler(confirmation = {}) {
    const {
        confirmTitle = i18n.t('common.unsavedTitle'),
        confirmBody = i18n.t('common.unsavedBody'),
        confirmOk = i18n.t('common.ok'),
        confirmCancel = i18n.t('common.cancel'),
    } = confirmation;

    let confirmMessage = {
        confirmTitle,
        confirmBody,
        confirmOk,
        confirmCancel,
    };

    return store.dispatch('confirm/confirmation', confirmMessage).then(r => {
        if (r) {
            return Promise.resolve();
        }

        return Promise.reject();
    });
}

/**
 * Common error handler
 * @param {Number} status error number
 * @param {string} routeName route name
 * @param {Function} replace
 */
export function errorHandler(status, routeName) {
    if (status === 400 || status === 403 || status === 404 || status === 409) {
        router.replace({ name: routeName });
    }
}
/**
 * Function for the requests catch block
 * @param {*} options {   // default values:
 *  reject: false,        // Promise reject if canceled
 *  showPopUp: true,      // Show error pop-up
 *  hideLoader: true,     // Set 'SHOW_LOADER' to false
 *  callback: function()  // function to execute if not canceled
 *  callbackAlways: function()  // function to execute in any case, as first thing
 * }
 * @returns callback(response) || response || promise reject
 */
export function ifIsNotCanceled(options = {}) {
    return response => {
        if (typeof options.callbackAlways === 'function') {
            options.callbackAlways(response);
        }
        if (response !== 'canceled') {
            if (options.hideLoader === undefined || options.hideLoader) {
                store.commit('SHOW_LOADING', false);
            }
            if (options.showPopUp === undefined || options.showPopUp) {
                let text;
                if (response.data && response.data.error) {
                    text = i18n.t(response.data.error);
                } else {
                    text = i18n.t(response.statusText);
                }
                store.dispatch(
                    'popup/popup',
                    {
                        type: ERROR,
                        header: i18n.t('common.error'),
                        text: text,
                    },
                    { root: true }
                );
            }
            if (options.callback !== undefined) {
                return options.callback(response);
            }
            return response;
        }
        if (options.reject !== undefined || options.reject) {
            return Promise.reject(response);
        }
        return response;
    };
}

/**
 * Checks incoming timestamp on valid, then returns the date string in the required format.
 * @param {number} timestamp
 * @param {string} dateFormat
 * @return {string} of date
 */
export function timestampToFormattedDateString(timestamp, dateFormat = '') {
    if (isValid(timestamp)) {
        try {
            return format(timestamp, dateFormat !== '' ? dateFormat : getFullDateFormat());
        } catch (e) {
            if (e instanceof RangeError) {
                return '';
            }
            throw e;
        }
    }
    return '';
}

/**
 * Checks incoming string with data on valid, then returns the date string in the required format.
 * @param {string} dateString
 * @param {string} dateFormat
 * @return {string} of date
 */
export function formatDateString(dateString, dateFormat = '') {
    let date = new Date(dateString);
    if (!Number.isNaN(date.getTime())) {
        return format(date, dateFormat !== '' ? dateFormat : getFullDateFormat());
    }
    return '';
}

export function getTimeString(dateString, timeFormat = '') {
    timeFormat = timeFormat !== '' ? timeFormat : getTimeFormat();
    if (typeof dateString !== 'string' || dateString === '' || dateString === undefined) return '';
    if (dateString.includes('/')) {
        // Here if second (extra) conversion of dateString ('/' was added into it after the first one).
        // The format() does not understand '/' between DATE and TIME parts (and even multiple spaces).
        let dateStringFixed = dateString
            .split('/')
            .join(' ')
            .replace(/  +/g, ' ');
        return format(parseISO(dateStringFixed), timeFormat);
    }
    return format(parseISO(dateString), timeFormat);
}

export function getDateFormat() {
    return store.state.timeFormat.date;
}

export function getTimeFormat() {
    return store.state.timeFormat.time;
}

export function getTimeNoSecondsFormat() {
    if (store.state.timeFormat.time) {
        return store.state.timeFormat.time.replace(':ss', '');
    }
    return 'HH:mm';
}

export function getFullDateFormat() {
    return getDateFormat() + store.state.timeFormat.separator + getTimeFormat();
}

export function getFullDateFormatNoSeconds() {
    return store.state.timeFormat.date + store.state.timeFormat.separator + getTimeNoSecondsFormat();
}

export function formatTime(h, m, formatString = '') {
    formatString = formatString === '' ? getTimeNoSecondsFormat() : formatString;
    if (m === undefined) {
        return getTimeString('2021-11-11T' + h + ':00', formatString);
    }
    h = h < 10 ? `0${h}` : h;
    return getTimeString('2021-11-11T' + h + ':' + m + ':00', formatString);
}
/**
 * Scroll to element
 * @param {HTMLHeadingElement} ref
 */
export function doScroll(ref) {
    let isIE = !!document.documentMode;
    let isEdge = !isIE && !!window.StyleMedia;
    let isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
    if (isIE || isEdge) {
        ref.scrollIntoView({ inline: 'end' });
    } else if (isFirefox) {
        ref.scrollIntoView({ block: 'start' });
    } else {
        ref.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
}

/**
 * Converts seconds to mm:ss in inputs
 * @param sec
 * @returns {string}
 */
export function secondsToMMSS(sec) {
    // Break s into minutes:seconds
    let m = Math.floor(sec / 60);
    let s = Math.floor(sec % 60);
    return [m, s].map(item => String(item).padStart(2, '0')).join(':');
}

/**
 * returns seconds in mm:ss or hh:mm:ss format
 * @param {Number} seconds
 * @return {string}
 */
export function secsToReadableTime(seconds) {
    const hour = 3600;

    if (seconds >= hour && seconds < hour * 10) {
        return '0' + Math.floor(seconds / hour) + ':' + secondsToMMSS(seconds % hour);
    }

    if (seconds >= hour * 10) {
        return Math.floor(seconds / hour) + ':' + secondsToMMSS(seconds % hour);
    }

    return secondsToMMSS(seconds);
}

/**
 * Returns natural sorted array of object/strings
 * @param {*} locations array of strings or objects with 'name' values
 */
export function sortLocations(locations) {
    return locations
        ? locations.sort((a, b) => {
              let nameA = a.name || a.loc_name || a;
              let nameB = b.name || b.loc_name || b;
              return nameA.localeCompare(nameB, undefined, {
                  numeric: true,
                  sensitivity: 'base',
              });
          })
        : [];
}

/**
 * @return {String}
 */
export function getFileExtension(file) {
    if (file && typeof file.name === 'string' && file.name.includes('.')) {
        return file.name.split('.').pop();
    }

    return '';
}

/**
 * @param {Integer} locationId
 * @return {Array of Strings}
 */
export function getLocationPath(location, locations, path = []) {
    if (location.type === 1) {
        // if location is AA
        return path;
    }

    let parent = locations.find(item => item.id === location.parent);

    if (parent) {
        return getLocationPath(parent, locations, [parent.name].concat(path));
    }

    return path;
}
